import React, { useState, useEffect, useRef } from 'react';
import './JavaDeepDive.css';
import { sections } from './javaDeepDiveData';
import { useInterview } from './useInterviewContext';

function AccordionItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`jd-accordion-item ${open ? ' open' : ''}`}>
            <button className="jd-accordion-trigger" onClick={() => setOpen(o => !o)}>
                <span className="jd-accordion-q">{q}</span>
                <span className="jd-accordion-icon" role="img" aria-label="expand">+</span>
            </button>
            <div className="jd-accordion-body">
                <div className="jd-accordion-content">
                    <div className="jd-answer-label"><span role="img" aria-label="check">✅</span> Key Insight</div>
                    <div className="jd-answer-text" dangerouslySetInnerHTML={{
                        __html: a.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>')
                    }} />
                </div>
            </div>
        </div>
    );
}

export default function JavaDeepDive() {
    const { toggleCoach, setActiveTopic, setCoachMode } = useInterview();
    const [activeId, setActiveId] = useState('oops');
    const refs = useRef({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const obs = new IntersectionObserver(
            es => es.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }),
            { rootMargin: '-15% 0px -75% 0px' }
        );
        Object.values(refs.current).forEach(el => el && obs.observe(el));
        return () => obs.disconnect();
    }, []);

    const scrollTo = id => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="jd-page">
            <div className="jd-topbar">
                <button className="jd-back-btn" onClick={() => { window.location.hash = 'swe'; }}>← Back to SWE</button>
                <div style={{ textAlign: 'center' }}>
                    <div className="jd-topbar-title"><span role="img" aria-label="java-coffee">☕</span> Java & OOPs Deep Dive</div>
                    <div className="jd-topbar-subtitle">OOPs · Collections · JVM · Concurrency</div>
                </div>
                <div style={{ width: 130 }} />
            </div>

            <div className="jd-body">
                <nav className="jd-sidebar">
                    <div className="jd-sidebar-title">Modules</div>
                    {sections.map(n => (
                        <button key={n.id} className={`jd-nav-item ${activeId === n.id ? ' active' : ''}`} onClick={() => scrollTo(n.id)}>
                            {n.icon} {n.title}
                        </button>
                    ))}
                </nav>

                <main className="jd-main">
                    {sections.map((s) => (
                        <section key={s.id} id={s.id} className="jd-section" ref={el => (refs.current[s.id] = el)}>
                            <div className="jd-section-header">
                                <div className="jd-section-icon" style={{ background: s.bg }}>{s.icon}</div>
                                <div className="jd-section-meta">
                                    <h2>{s.icon} {s.title} <span className="jd-tagline">— {s.tagline}</span></h2>
                                </div>
                                <button
                                    className="jd-practice-btn"
                                    onClick={() => {
                                        setActiveTopic({ ...s, type: 'swe' });
                                        setCoachMode('learn');
                                        toggleCoach(true);
                                    }}
                                >
                                    🤖 Learn with AI
                                </button>
                            </div>

                            <div className="jd-simple-box">
                                <div className="jd-simple-label"><span role="img" aria-label="book">📖</span> Concept</div>
                                <p>{s.simple}</p>
                            </div>

                            <div className="jd-key-points">
                                <div className="jd-simple-label"><span role="img" aria-label="target">🎯</span> Essential Points</div>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {s.keyPoints.map((p, i) => (
                                        <li key={i} style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.75rem', color: '#94a3b8' }}>
                                            <span style={{ color: s.bg }}>•</span> {p}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="jd-code-block">
                                <div className="jd-code-label"><span role="img" aria-label="laptop">💻</span> Code Blueprint</div>
                                <pre>{s.code}</pre>
                            </div>

                            <div className="jd-qa-section">
                                <div className="jd-simple-label"><span role="img" aria-label="question">❓</span> Common Questions</div>
                                <div className="jd-accordion">
                                    {s.qa.map((q, i) => (
                                        <AccordionItem key={i} q={q.q} a={q.a} />
                                    ))}
                                </div>
                            </div>
                        </section>
                    ))}
                </main>
            </div>
        </div>
    );
}
